import { cursorTarget, withoutLabels } from "@corca/data/libraries/item";

import { LIBRARY_SERVES } from "@/components/suggestions/providers/librarySuggestionsProvider/candidates";
import { isSelectionLocked } from "@/components/tiptap/lock/utils";
import { applySuggestion } from "@/input/utils/editOperators";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { DecoratorNodeModel } from "@/models/nodes/DecoratorNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { hasUnit } from "@/models/nodes/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { detectLanguage } from "@/utils/detectLanguage";
import { getRandomTailwindColor } from "@/utils/tailwindColors";

import { splitInlineExpression } from "../input/utils/splitInlineExpression";

import { isGridOptionSiggestion } from "./list/utils";
import { getProviders } from "./providers";
import { decoratorsJson } from "./providers/data";
import { checkNodeCondition } from "./providers/utils/nodeCondition";

import type {
  Suggestion,
  SingleSuggestion,
  SuggestionNodeType,
  SuggestionProvider,
  SuggestionProviderType,
  SuggestionsGroup,
  ProviderSearchOptions,
} from "@/components/suggestions/types";
import type { ActiveCursorModel } from "@/models/CursorModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorState } from "@/models/EditorModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson, InsertNode, SubstitutionRule, SuggestionPopupGroup } from "@corca/data";

export function isSuggestionOfType<T extends SuggestionNodeType>(
  s: Suggestion,
  type: T,
): s is SingleSuggestion<T> {
  return s.type === "single" && s.item.type === type;
}

export function nestSuggestions<T extends SuggestionNodeType>(
  input: string,
  suggestions: Suggestion<T>[],
): Suggestion<T>[] {
  const nestedGroups = new Map<string, SingleSuggestion<T>[]>();
  const result: Suggestion<T>[] = [];

  for (const suggestion of suggestions) {
    if (suggestion.type === "single" && suggestion.nestedKey) {
      const key = suggestion.nestedKey;
      if (!nestedGroups.has(key)) {
        nestedGroups.set(key, []);
      }
      nestedGroups.get(key)!.push(suggestion);
    } else {
      result.push(suggestion);
    }
  }

  for (const [, group] of nestedGroups) {
    const sorted = [...group].sort((a, b) => b.priority - a.priority);
    const nestedOnly = !!sorted[0]?.nestedOnly;
    const minSize = nestedOnly ? 1 : 2;

    if (group.length > minSize) {
      const remaining = nestedOnly ? sorted : sorted.slice(1);
      const root = remaining[0];

      if (!nestedOnly) {
        result.push(sorted[0]);
      }

      result.push({
        ...root,
        nestedItems: groupSuggestions(input, remaining),
      });
    } else {
      result.push(...group);
    }
  }

  return result;
}

export function flattenSuggestions<T extends SuggestionNodeType>(
  suggestions: Suggestion<T>[],
): Suggestion<T>[] {
  return suggestions.flatMap((suggestion) => {
    if (suggestion.type !== "single" || !suggestion.nestedItems?.length) {
      return [suggestion];
    }

    return flattenSuggestions(suggestion.nestedItems.flatMap((group) => group.items));
  });
}

export function groupSuggestions<T extends SuggestionNodeType>(
  input: string,
  suggestions: Suggestion<T>[],
  limit?: number,
): SuggestionsGroup<T>[] {
  const groupedSuggestions = suggestions.reduce<
    Partial<Record<SuggestionPopupGroup, SuggestionsGroup<T>>>
  >((acc, sugg) => {
    const group = sugg.popupGroup ?? "default";
    if (!acc[group]) {
      acc[group] = {
        name: group,
        view: isGridOptionSiggestion(sugg) ? "grid" : "list",
        items: [],
      };
    }

    acc[group].items.push(sugg);

    return acc;
  }, {});

  const filteredGroups = Object.values(groupedSuggestions);
  const sortedGroups = sortSuggestionGroups<T>(input, filteredGroups);

  return sortedGroups
    .map((group) => ({
      ...group,
      items: group.items.slice(0, limit),
    }))
    .reduce<SuggestionsGroup<T>[]>((acc, group, i) => {
      const prevGroup = acc[i - 1];
      const startIndex = (prevGroup?.startIndex ?? 0) + (prevGroup?.items.length ?? 0);

      return [...acc, { ...group, startIndex }];
    }, []);
}

export function sortSuggestions<T extends SuggestionNodeType>(
  input: string,
  suggestions: Suggestion<T>[],
) {
  return [...suggestions].sort((a, b) => {
    if (b.priority === a.priority) {
      const aMatched = a.matchedString ?? "";
      const bMatched = b.matchedString ?? "";

      if (aMatched.length > 0 && bMatched.length > 0) {
        const aFirstWordLength =
          aMatched.indexOf(" ") >= 0 ? aMatched.indexOf(" ") : aMatched.length;
        const bFirstWordLength =
          bMatched.indexOf(" ") >= 0 ? bMatched.indexOf(" ") : bMatched.length;

        // "Divide" wins over "Divide Number" (first word same length)
        if (aFirstWordLength === bFirstWordLength) {
          return aMatched.length - bMatched.length;
        }

        // "Divide By" wins over "Divides" (first word shorter)
        return aFirstWordLength - bFirstWordLength;
      }
    }

    return b.priority - a.priority;
  });
}

const GROUP_ORDER: SuggestionPopupGroup[] = [
  "insertMath",
  "variableArguments",
  "hints",
  "convertNode",
  "inlineTextStyle",
  "labels",
  "clipboard",
  "graph",
  "apply",
  "functionBrackets",
  "transform",
];

const ORDERED_GROUP_RANK_BASE = 100;

function groupRank<T extends SuggestionNodeType>(group: SuggestionsGroup<T>): number {
  const index = GROUP_ORDER.indexOf(group.name);
  if (index === -1) return group.items[0].priority;

  return ORDERED_GROUP_RANK_BASE + GROUP_ORDER.length - index;
}

export function sortSuggestionGroups<T extends SuggestionNodeType>(
  input: string,
  groups: SuggestionsGroup<T>[],
): SuggestionsGroup<T>[] {
  // the multi provider curates its own order (structural variants of the best
  // function first), so keep it instead of re-sorting by priority
  const groupsWithSortedItems = groups.map((group) => ({
    ...group,
    items: group.name === "multi" ? group.items : sortSuggestions(input, group.items),
  }));

  return [...groupsWithSortedItems].sort((a, b) => {
    return groupRank(b) - groupRank(a);
  });
}

export function shouldAcceptSuggestions(
  type: SuggestionProviderType,
  accept: SuggestionProviderType[],
  editorState: EditorState,
) {
  if (editorState.latexInput) {
    return type === "latexInput";
  }

  if (type === "latexInput") return false;

  if (accept.length === 0 || accept.includes(type)) return true;

  return type === "library" && accept.some((accepted) => LIBRARY_SERVES.has(accepted));
}

function acceptedProviders(doc: DocumentModel, accept: SuggestionProviderType[]) {
  const locked = isSelectionLocked(doc);

  return getProviders().filter(
    (provider) =>
      (!locked || provider.allowedWhenLocked) &&
      shouldAcceptSuggestions(provider.type, accept, doc.editor.editorState),
  );
}

export function groupSuggestionsByGroupKey(suggestions: SingleSuggestion[]) {
  const groupIndexes = new Map<string, number>();

  return suggestions.reduce<(SingleSuggestion | SingleSuggestion[])[]>((acc, suggestion) => {
    if (suggestion.sidebarGroupKey) {
      if (!groupIndexes.has(suggestion.sidebarGroupKey)) {
        groupIndexes.set(suggestion.sidebarGroupKey, acc.length);
        acc.push([suggestion]);
      } else {
        const groupIndex = groupIndexes.get(suggestion.sidebarGroupKey)!;
        (acc[groupIndex] as SingleSuggestion[]).push(suggestion);
      }
    } else {
      acc.push(suggestion);
    }

    return acc;
  }, []);
}

/**
 * Search all providers and return chunks of suggestions (sync and async)
 */
export function searchSuggestionsWithAsync<T extends SuggestionNodeType>(
  search: string,
  doc: DocumentModel,
  accept: SuggestionProviderType[],
  providerSearchOptions?: ProviderSearchOptions,
  signal?: AbortSignal,
): Array<Suggestion<T>[] | Promise<Suggestion<T>[]>> {
  const lang = detectLanguage(search);
  const chunks: Array<Suggestion<T>[] | Promise<Suggestion<T>[]>> = [];
  for (const provider of acceptedProviders(doc, accept)) {
    const providerOptions =
      provider.type === "library"
        ? { ...providerSearchOptions?.library, accept }
        : providerSearchOptions?.[provider.type as keyof ProviderSearchOptions];

    const result = (provider as SuggestionProvider<SuggestionProviderType, T>).search(
      search,
      doc,
      lang,
      providerOptions,
      signal,
    );
    chunks.push(...result);
  }
  return chunks;
}

/**
 * Search all providers and return only sync suggestions (ignores async chunks)
 */
export function searchSuggestions<T extends SuggestionNodeType>(
  search: string,
  doc: DocumentModel,
  accept: SuggestionProviderType[],
  providerSearchOptions?: ProviderSearchOptions,
) {
  const lang = detectLanguage(search);
  const filtered: Suggestion<T>[] = [];
  for (const provider of acceptedProviders(doc, accept)) {
    const providerOptions =
      provider.type === "library"
        ? { ...providerSearchOptions?.library, accept }
        : providerSearchOptions?.[provider.type as keyof ProviderSearchOptions];

    const result = (provider as SuggestionProvider<SuggestionProviderType, T>).search(
      search,
      doc,
      lang,
      providerOptions,
    );

    // Only collect sync suggestions, ignore promises
    for (const chunk of result) {
      if (Array.isArray(chunk)) {
        filtered.push(...chunk);
      }
    }
  }
  return filtered;
}

/**
 * Whether picking this suggestion replaces the typed text with a math node (number,
 * variable, function, expression, …), rather than inserting a symbol as text or
 * running a slash command.
 */
export function isMathConversionSuggestion(suggestion: Suggestion): boolean {
  return suggestion.type === "multi" || (suggestion.type === "single" && !suggestion.item.isSymbol);
}

export function transformSuggestion<T extends SuggestionNodeType>(
  value: string,
  suggestion: Suggestion<T>,
  nextSuggestion: Suggestion<T> | null,
): Suggestion<T> {
  if (nextSuggestion?.type !== "single" || !nextSuggestion.action?.type) return suggestion;

  switch (nextSuggestion.action.type) {
    case "remove-labels":
      if (suggestion.type === "single") {
        let updatedSuggestion: SingleSuggestion<T> = suggestion;

        if ((updatedSuggestion.item.labels?.length ?? 0) > 0) {
          updatedSuggestion = {
            ...updatedSuggestion,
            item: {
              ...updatedSuggestion.item,
              labels: [],
              node: undefined,
            },
          };
        }

        if (
          updatedSuggestion.action?.type === "insert-expression" &&
          updatedSuggestion.action.targetNode
        ) {
          const { targetNode, nodesJson } = updatedSuggestion.action;

          if (nodesJson.length === 1) {
            const hasLabels = !!targetNode.path.split("::").find((el) => el.startsWith("label"));

            if (hasLabels) {
              updatedSuggestion = {
                ...updatedSuggestion,
                action: {
                  ...updatedSuggestion.action,
                  targetNode: undefined,
                },
              };
            }
          }
        }

        const { item } = updatedSuggestion;
        if (item.type === "library" && item.nodesJson?.length === 1) {
          const nodesJson = withoutLabels(item.nodesJson);

          if (nodesJson !== item.nodesJson) {
            updatedSuggestion = {
              ...updatedSuggestion,
              item: {
                ...item,
                nodesJson,
                targetNode: cursorTarget(nodesJson),
              },
            };
          }
        }

        return updatedSuggestion;
      }
      break;
    case "open-group":
    case "apply-function":
      if (
        suggestion.type === "single" &&
        suggestion.item.node?.is(VariableNodeModel) &&
        suggestion.item.node.hasParams
      ) {
        return {
          ...suggestion,
          item: {
            ...suggestion.item,
            labels: [],
            node: undefined,
          },
        };
      }
      break;

    default:
      break;
  }

  return suggestion;
}

function fillDefaults(doc: DocumentModel, node: InsertNode): NodeJson {
  const { type, children = [] } = node;
  let props = node.props ?? {};

  if (node.type === "variable" && !node.props?.color) {
    const childProps = children[0]?.props ?? {};
    const value =
      "value" in childProps && typeof childProps.value === "string" ? childProps.value : undefined;

    const color = VariableNodeModel.getVariableByValue(doc, value)?.color;

    props = { ...node.props, color: color ?? getRandomTailwindColor(value) } as typeof props;
  }

  return {
    type,
    props,
    children: children.map((c) => fillDefaults(doc, c)),
  } as NodeJson;
}

export function insertNodeChildren(
  doc: DocumentModel,
  node: NodeModel,
  insertChildren: SubstitutionRule["insertChildren"],
) {
  if (!insertChildren) return;

  let children = "parameters" in node ? node.parameters : node.nodes;

  if (children.length < insertChildren.length) {
    for (let i = 0; i < insertChildren.length - children.length; i++) {
      node.appendChild(
        node.is(GroupNodeModel) ? MatrixNodeModel.create(doc) : InlineNodeModel.create(doc),
      );
    }
  }

  children = "parameters" in node ? node.parameters : node.nodes;

  for (let i = 0; i < children.length; i += 1) {
    const arr = insertChildren[i];
    if (arr) {
      const nodes = arr
        .map((nodeJson) => createNodeFrom(fillDefaults(doc, nodeJson), doc))
        .filter(Boolean);
      children[i].appendChildren(nodes);
    }
  }
}

export function setNodeChildren(
  doc: DocumentModel,
  node: NodeModel,
  setChildren?: SubstitutionRule["setChildren"],
) {
  if (!setChildren) {
    return false;
  }

  // TODO. Check for nodes with symbolNode
  const filtered = setChildren.skipChildNodes
    ? node.nodes.filter((n) => checkNodeCondition(setChildren.skipChildNodes, n))
    : [];

  const nodes = setChildren.children
    .map((child) => {
      if (!child) return null;
      return createNodeFrom(fillDefaults(doc, child), doc);
    })
    .filter((n) => !!n);

  node.setChildren([...filtered, ...nodes]);

  return true;
}

export function setNodeDecorators(
  doc: DocumentModel,
  node?: NodeModel,
  insertDecorators?: SubstitutionRule["insertDecorators"],
) {
  if (!insertDecorators) {
    return false;
  }

  let applyTo: NodeModel | undefined = node;

  if (node && "symbolNode" in node && node.symbolNode) {
    applyTo = node.symbolNode;
  }

  if (applyTo && "decorators" in applyTo) {
    const existingDecorators = new Set(applyTo.decorators.map((d) => d.name));
    const decoratorsToAdd = insertDecorators
      .map((d) => d.name)
      .filter((name) => !existingDecorators.has(name));

    applyTo.decorators.forEach((decorator) => {
      doc.removeNode(decorator);
    });

    const added = decoratorsToAdd.map((name) => {
      const decoratorJson = decoratorsJson[name];
      return doc.create(DecoratorNodeModel, name, { key: decoratorJson.key });
    });

    if (added.length > 0) {
      applyTo.appendChildren(added);
    }

    return true;
  }

  return false;
}

export function insertNodeFromSuggestion(
  cursor: ActiveCursorModel,
  newNode: NodeModel,
  suggestion?: SingleSuggestion,
  skipCursorMove?: boolean,
): { node: NodeModel | undefined; cursorMoved: boolean } {
  const { applyTo } = suggestion?.substitution ?? {};
  const { node, cursorMoved } = applySuggestion(
    cursor,
    applyTo ? null : newNode,
    suggestion?.substitution,
    skipCursorMove,
  );
  if (node) {
    insertNodeChildren(cursor.doc, node, suggestion?.item.insertChildren);
    setNodeChildren(cursor.doc, node, suggestion?.item.setChildren);
  }
  return { node, cursorMoved };
}

export function splitInlineEditable(
  doc: DocumentModel,
  node: NodeModel,
  input: HTMLInputElement,
  char: string,
) {
  if (!node.isInlineEditable() || !node.isSplittable) return false;

  const { selectionStart, selectionEnd, value } = input;
  doc.editor.resetInput(true);
  const valueBefore = value.substring(0, selectionStart ?? 0);
  const valueAfter = value.substring(selectionEnd ?? 0);

  if (!valueBefore) {
    doc.cursor.moveBeforeNode(node);
    if (selectionStart !== selectionEnd) {
      doc.transact(() => {
        if (valueAfter.length === 0) {
          doc.removeNode(node);
        }
        node.value = valueAfter;
      }, "splitInlineEditable");
    }
    if (char === " ") {
      splitInlineExpression(doc);
      return true;
    }
  } else if (!valueAfter) {
    if (selectionStart !== selectionEnd) {
      doc.transact(() => {
        if (valueBefore.length === 0) {
          doc.removeNode(node);
        }
        node.value = valueBefore;
      }, "splitInlineEditable");
    }
    doc.cursor.moveAfterNode(node);
    if (char === " ") {
      splitInlineExpression(doc);
      return true;
    }
  } else {
    return doc.transact(() => {
      if (char !== " ") {
        if (node.validateValue(valueBefore + char)) {
          const numberAfter = createNodeFrom(node, doc, { resetIds: true });
          numberAfter.value = valueAfter;
          node.insertSiblingAfter(numberAfter);
          node.value = valueBefore + char;

          if (hasUnit(node, "prefix")) {
            numberAfter.setChildren([]);
          } else {
            node.setChildren([]);
          }

          doc.cursor.moveBeforeNode(node);
          doc.input.editNode(node, node.value.length);
          return true;
        }

        if (node.validateValue(char + valueAfter)) {
          const numberAfter = createNodeFrom(node, doc, { resetIds: true });
          numberAfter.value = char + valueAfter;
          node.insertSiblingAfter(numberAfter);
          node.value = valueBefore;

          if (hasUnit(node, "prefix")) {
            numberAfter.setChildren([]);
          } else {
            node.setChildren([]);
          }

          doc.cursor.moveBeforeNode(numberAfter);
          doc.input.editNode(numberAfter, 1);
          return true;
        }
      }

      const numberAfter = createNodeFrom(node, doc, { resetIds: true });
      numberAfter.value = valueAfter;
      node.insertSiblingAfter(numberAfter);
      node.value = valueBefore;

      if (hasUnit(node, "prefix")) {
        numberAfter.setChildren([]);
      } else {
        node.setChildren([]);
      }

      doc.cursor.moveBeforeNode(numberAfter);

      if (char === " ") {
        splitInlineExpression(doc);
        return true;
      }
      return false;
    }, "splitInlineEditable");
  }

  return false;
}
